import { Button, Icon24ArrowRight, Typography } from "@cur8/maneki";
import { useLocation } from "@pomle/react-router-paths";
import {
  Labels,
  useImmediateRiskMutation,
} from "hooks/mutations/useImmediateRiskMutation";
import { useLatestConsentsQuery } from "hooks/queries/usePatientConsentsQuery";
import { useVisitQuery } from "hooks/queries/useVisitQuery";
import { getSafeReturnPath } from "lib/getSafeReturnPath";
import { CheckInParams } from "lib/types/checkInParameters";
import { ImmediateRiskAssessment } from "lib/types/immediate-risk-assessment";
import { useCallback, useMemo, useState } from "react";
import { useAsyncHandle } from "render/hooks/useAsyncHandle";
import { paths } from "render/routes/paths";
import { shouldCounterSignBeforeRoom } from "render/routes/shouldCounterSignBeforeRoom";
import { ActionsFooter } from "render/ui/layout/ActionsFooter/ActionsFooter";
import { InformationLayout } from "render/ui/layout/InformationLayout";
import { PageFrameContent } from "render/ui/layout/PageFrameContent/PageFrameContent";
import { BinaryRadio } from "render/ui/presentation/BinaryRadio";
import { Group, getGroupHighlight } from "render/ui/presentation/Group";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

type ImmediateRiskAssessmentProps = {
  goTo(url: string): void;
  onBack(): void;
} & CheckInParams;

const DEFAULT_ERROR_MESSAGE = "Please answer this question";

type Errors = {
  [key in keyof ImmediateRiskAssessment]: string | undefined;
};

const validate = (risk: ImmediateRiskAssessment) => {
  const newErrors = {} as Errors;

  if (risk.acuteSymptoms === undefined) {
    newErrors.acuteSymptoms = DEFAULT_ERROR_MESSAGE;
  }

  if (risk.medicationUsageProneToBleeding === undefined) {
    newErrors.medicationUsageProneToBleeding = DEFAULT_ERROR_MESSAGE;
  }

  return newErrors;
};

const INITIAL_RISK_ASSESSMENT: ImmediateRiskAssessment = {
  acuteSymptoms: undefined,
  medicationUsageProneToBleeding: undefined,
};

export function ImmediateRiskAssessmentView({
  goTo,
  onBack,
  patientId,
  visitId,
}: ImmediateRiskAssessmentProps) {
  const [risk, setRisk] = useState<ImmediateRiskAssessment>(
    INITIAL_RISK_ASSESSMENT
  );
  const immediateRiskMutation = useImmediateRiskMutation();
  const [errors, setErrors] = useState<Errors>();

  const { data: latestConsents, isSuccess: consentsLoaded } =
    useLatestConsentsQuery({
      patientId: patientId,
    });

  const { data: visit, isSuccess: visitLoaded } = useVisitQuery({
    patientId: patientId,
    visitId: visitId,
  });

  const location = useLocation();

  // A safe returnPath already names the destination; only the fallback needs
  // the visit and consents, so only then is Continue held until they load.
  const hasSafeReturnPath =
    getSafeReturnPath(location.searchParams.get("returnPath"), "") !== "";
  const destinationKnown = hasSafeReturnPath || (visitLoaded && consentsLoaded);

  const url = useMemo(() => {
    const fallback = shouldCounterSignBeforeRoom({
      visitConsents: visit?.consents,
      latestConsents,
    })
      ? paths.counterSign.url({ patientId, visitId })
      : visit?.extendedBloodPanel
        ? paths.extendedVisitLimsBloodDrawConfirm.url({ patientId, visitId })
        : paths.assignToRoom.url({ patientId, visitId });
    return getSafeReturnPath(location.searchParams.get("returnPath"), fallback);
  }, [
    latestConsents,
    location.searchParams,
    patientId,
    visit?.consents,
    visit?.extendedBloodPanel,
    visitId,
  ]);

  const handleContinue = useAsyncHandle(
    useCallback(async () => {
      if (!risk || !destinationKnown) {
        return;
      }

      const errors = validate(risk);
      const hasErrors = Object.keys(errors).length > 0;

      if (hasErrors) {
        setErrors(errors);
        return;
      }

      try {
        await immediateRiskMutation.mutateAsync({
          patientId: patientId,
          visitId: visitId,
          assessment: risk,
        });
      } catch {
        // Already reported via the mutation's own onError. Stay on this
        // screen rather than navigating on as if the answers were saved.
        return;
      }

      goTo(url);
    }, [
      destinationKnown,
      goTo,
      immediateRiskMutation,
      patientId,
      risk,
      url,
      visitId,
    ])
  );

  return (
    <PageFrameContent>
      <InformationLayout
        content={
          <section className={styles.layout}>
            <Typography variant="display-s">
              <Trans.Title />
            </Typography>

            <div className={styles.questions}>
              <Group
                highlight={getGroupHighlight({
                  value: risk?.acuteSymptoms,
                  hasError: Boolean(errors?.acuteSymptoms),
                })}
              >
                <BinaryRadio
                  name="immediate_risk_assessment.acuteSymptoms"
                  label={Labels.AcuteSymptoms}
                  value={risk?.acuteSymptoms}
                  error={errors?.acuteSymptoms}
                  onChange={(value) => {
                    setRisk((prev) => ({ ...prev, acuteSymptoms: value }));
                    if (errors) {
                      delete errors["acuteSymptoms"];
                    }
                  }}
                />
              </Group>
              <Group
                highlight={getGroupHighlight({
                  value: risk?.medicationUsageProneToBleeding,
                  hasError: Boolean(errors?.medicationUsageProneToBleeding),
                })}
              >
                <BinaryRadio
                  name="immediate_risk_assessment.medicationUsageProneToBleeding"
                  label={Labels.MedicationUsageProneToBleeding}
                  value={risk?.medicationUsageProneToBleeding}
                  error={errors?.medicationUsageProneToBleeding}
                  onChange={(value) => {
                    setRisk((prev) => ({
                      ...prev,
                      medicationUsageProneToBleeding: value,
                    }));
                    if (errors) {
                      delete errors["medicationUsageProneToBleeding"];
                    }
                  }}
                />
              </Group>
            </div>

            {immediateRiskMutation.isError ? (
              <Typography variant="body-s" color="warning">
                Failed to save. Please try again.
              </Typography>
            ) : undefined}
          </section>
        }
        buttons={
          <ActionsFooter
            left={
              <Button variant="secondary" onPress={onBack} fillContainer>
                Back
              </Button>
            }
            right={
              <Button
                state={
                  handleContinue.busy
                    ? "loading"
                    : !destinationKnown
                      ? "disabled"
                      : undefined
                }
                onPress={handleContinue.run}
                fillContainer
                icon={<Icon24ArrowRight />}
              >
                Continue
              </Button>
            }
          />
        }
      />
    </PageFrameContent>
  );
}
